import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus, Loader2 } from 'lucide-react'
import { useUser } from '@/shared/hooks/useUser'
import { useBots } from '@/shared/hooks/useBots'
import { useTelegram } from '@/shared/hooks/useTelegram'
import BotCard from '@/components/bots/BotCard'
import Button from '@/components/ui/Button'
import AddBotView from '@/components/bots/AddBotView'
import PullToRefresh from '@/components/ui/PullToRefresh'
import { botsApi } from '@/shared/api/bots'
import type { Bot } from '@/shared/types'

export default function BotsList() {
  const navigate = useNavigate()
  const { hapticFeedback, hapticNotification, telegramId } = useTelegram()
  const { data: user, isLoading: userLoading, error: userError } = useUser(telegramId)
  const { data: ownedBots, isLoading: botsLoading, error: botsError, refetch: refetchOwned } = useBots(user?.id)
  const [showAddBot, setShowAddBot] = useState(false)
  const [showUserLoader, setShowUserLoader] = useState(false)
  const [showBotsLoader, setShowBotsLoader] = useState(false)
  const [accessibleBots, setAccessibleBots] = useState<Bot[]>([])

  // Delay showing user loader to prevent flash
  useEffect(() => {
    if (userLoading) {
      const timer = setTimeout(() => setShowUserLoader(true), 200)
      return () => clearTimeout(timer)
    } else {
      setShowUserLoader(false)
    }
  }, [userLoading])

  // Delay showing bots loader to prevent flash
  useEffect(() => {
    if (botsLoading) {
      const timer = setTimeout(() => setShowBotsLoader(true), 200)
      return () => clearTimeout(timer)
    } else {
      setShowBotsLoader(false)
    }
  }, [botsLoading])

  const handleBotClick = (botId: string) => {
    hapticFeedback('light')
    navigate(`/bots/${botId}`)
  }

  const handleAddBot = () => {
    hapticFeedback('light')
    setShowAddBot(true)
  }

  const handleCloseAddBot = () => {
    setShowAddBot(false)
  }

  // Fetch accessible bots
  useEffect(() => {
    const fetchAccessibleBots = async () => {
      if (!user) return

      try {
        const bots = await botsApi.listBotsWithAccess()
        // Mark bots with user_role
        const botsWithRole = bots.map(bot => ({
          ...bot,
          user_role: 'editor' as const // Default to editor, backend could provide actual role
        }))
        setAccessibleBots(botsWithRole)
      } catch (error) {
        console.error('Failed to fetch accessible bots:', error)
        setAccessibleBots([])
      }
    }

    fetchAccessibleBots()
  }, [user])

  const handleRefresh = async () => {
    try {
      // Refresh both owned and accessible bots
      await Promise.all([
        refetchOwned(),
        (async () => {
          const bots = await botsApi.listBotsWithAccess()
          const botsWithRole = bots.map(bot => ({ ...bot, user_role: 'editor' as const }))
          setAccessibleBots(botsWithRole)
        })()
      ])
      hapticNotification?.('success')
    } catch (error) {
      console.error('Failed to refresh bots:', error)
      hapticNotification?.('error')
    }
  }

  console.log('BotsList render - user:', user, 'userLoading:', userLoading, 'userError:', userError)
  console.log('BotsList render - ownedBots:', ownedBots, 'botsLoading:', botsLoading, 'botsError:', botsError)
  console.log('BotsList render - accessibleBots:', accessibleBots)
  console.log('BotsList render - showAddBot:', showAddBot)

  // Mark owned bots with user_role
  const ownedBotsWithRole = (ownedBots || []).map(bot => ({
    ...bot,
    user_role: 'owner' as const
  }))

  // Combine and deduplicate bots
  const hasOwnedBots = ownedBotsWithRole.length > 0
  const hasAccessibleBots = accessibleBots.length > 0

  // Show loading if user is loading and loader should be visible
  if ((userLoading || (!user && !userError)) && showUserLoader) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-tg-button" />
      </div>
    )
  }

  // Return null during initial load before loader timeout
  if (userLoading || (!user && !userError)) {
    return null
  }

  if (userError) {
    return (
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="text-center">
          <div className="text-4xl mb-4">⚠️</div>
          <h2 className="text-lg font-semibold text-tg-text mb-2">Ошибка загрузки пользователя</h2>
          <p className="text-sm text-tg-hint">{userError.message}</p>
        </div>
      </div>
    )
  }

  if (!user) {
    return (
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="text-center">
          <div className="text-4xl mb-4">👤</div>
          <h2 className="text-lg font-semibold text-tg-text mb-2">Пользователь не найден</h2>
          <p className="text-sm text-tg-hint">Пожалуйста, запустите бота в Telegram</p>
        </div>
      </div>
    )
  }

  // Only show bots loading state after user is loaded
  if (botsLoading && showBotsLoader) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-tg-button" />
      </div>
    )
  }

  if (botsLoading) {
    return null
  }

  if (botsError) {
    return (
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="text-center">
          <div className="text-4xl mb-4">⚠️</div>
          <h2 className="text-lg font-semibold text-tg-text mb-2">Ошибка загрузки ботов</h2>
          <p className="text-sm text-tg-hint">{botsError.message}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-tg-bg">
      <PullToRefresh onRefresh={handleRefresh}>
        <div className="p-4 pb-24 pt-[96px]">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h1 className="text-2xl font-bold text-tg-text">Мои боты</h1>
                <p className="text-sm text-tg-hint mt-1">Управляйте вашими ботами</p>
              </div>
              <button
                onClick={handleAddBot}
                className="flex items-center justify-center w-12 h-12 bg-tg-button hover:opacity-90 text-tg-button-text rounded-xl shadow-lg shadow-tg-button/25 active:scale-95 transition-all"
              >
                <Plus className="w-6 h-6" />
              </button>
            </div>

            {!hasOwnedBots && !hasAccessibleBots ? (
              <div className="text-center py-12">
                <div className="text-6xl mb-4">🤖</div>
                <h2 className="text-xl font-semibold text-tg-text mb-2">Нет ботов</h2>
                <p className="text-tg-hint mb-6">Добавьте своего первого бота для монетизации трафика</p>
                <Button onClick={handleAddBot}>
                  <Plus className="w-5 h-5" />
                  Добавить бота
                </Button>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Owned bots section - show header only if there are accessible bots too */}
                {hasOwnedBots && (
                  <div>
                    {hasAccessibleBots && (
                      <h2 className="text-sm font-semibold text-tg-hint uppercase tracking-wide mb-3">
                        Мои боты ({ownedBotsWithRole.length})
                      </h2>
                    )}
                    <div className="space-y-4">
                      {ownedBotsWithRole.map((bot) => (
                        <BotCard key={bot.id} bot={bot} onClick={() => handleBotClick(bot.id)} />
                      ))}
                    </div>
                  </div>
                )}

                {/* Accessible bots section */}
                {hasAccessibleBots && (
                  <div>
                    <h2 className="text-sm font-semibold text-tg-hint uppercase tracking-wide mb-3">
                      Доступные боты ({accessibleBots.length})
                    </h2>
                    <div className="space-y-4">
                      {accessibleBots.map((bot) => (
                        <BotCard key={bot.id} bot={bot} onClick={() => handleBotClick(bot.id)} />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Add Bot View - Always render */}
            <AddBotView
              isOpen={showAddBot}
              onClose={handleCloseAddBot}
              ownerId={user?.id || ''}
            />
          </div>
        </div>
      </PullToRefresh>
    </div>
  )
}
